"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/db/auth/client";
import { toast } from "@garagem/ui/components/sonner";

// Two-Factor Authentication hooks
export function useEnable2FA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      const response = await authClient.twoFactor.enable({ password });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (error) => {
      const errorCode = (error as { code?: string })?.code;
      const errorMessage = error?.message || "Erro ao ativar 2FA";

      if (errorCode === "INVALID_PASSWORD") {
        toast.error("Senha incorreta", {
          description: "Verifique sua senha e tente novamente",
        });
      } else if (errorCode === "TWO_FACTOR_ALREADY_ENABLED") {
        toast.error("2FA já está ativo", {
          description: "A autenticação de dois fatores já está configurada",
        });
      } else {
        toast.error("Falha ao ativar 2FA", {
          description: errorMessage,
        });
      }
    },
  });
}

export function useVerifyTOTP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ code }: { code: string }) => {
      const response = await authClient.twoFactor.verifyTotp({ code });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("2FA ativado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (error) => {
      const errorCode = (error as { code?: string })?.code;
      const errorMessage = error?.message || "Código inválido";

      if (errorCode === "INVALID_OTP" || errorCode === "INVALID_TOTP") {
        toast.error("Código inválido", {
          description: "Verifique o código e tente novamente",
        });
      } else if (errorCode === "EXPIRED_OTP") {
        toast.error("Código expirado", {
          description: "O código expirou, gere um novo código",
        });
      } else {
        toast.error("Falha na verificação", {
          description: errorMessage,
        });
      }
    },
  });
}

export function useDisable2FA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      const response = await authClient.twoFactor.disable({ password });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("2FA desativado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (error) => {
      const errorCode = (error as { code?: string })?.code;
      const errorMessage = error?.message || "Erro ao desativar 2FA";

      if (errorCode === "INVALID_PASSWORD") {
        toast.error("Senha incorreta", {
          description: "Verifique sua senha e tente novamente",
        });
      } else if (errorCode === "TWO_FACTOR_NOT_ENABLED") {
        toast.error("2FA não está ativo", {
          description: "A autenticação de dois fatores não está configurada",
        });
      } else {
        toast.error("Falha ao desativar 2FA", {
          description: errorMessage,
        });
      }
    },
  });
}

